/**
 * Transport stats -> SDK stats mapping and network-quality classification.
 *
 * The mapping is defensive: transports report whatever the media stack exposes, and the facade fills
 * absent fields with zero so callers can render a stable shape. Units are part of the contract:
 * milliseconds for `rtt`/`jitter`, a [0, 1] ratio for `packetLoss`, kbps for `bitrate`.
 */
import { NetworkQuality } from './types.js';
function nonNegative(value, fallback = 0) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return fallback;
    return Math.max(0, value);
}
function integer(value) {
    return Math.round(nonNegative(value));
}
/** Map raw transport counters to the public `RtcStats` shape. */
export function toRtcStats(input = {}) {
    const width = integer(input.width);
    const height = integer(input.height);
    return {
        rtt: nonNegative(input.rttMs ?? undefined),
        packetLoss: Math.min(1, nonNegative(input.packetLoss ?? undefined)),
        jitter: nonNegative(input.jitterMs ?? undefined),
        bitrate: nonNegative(input.bitrateKbps ?? undefined),
        resolution: { width, height },
        framesPerSecond: nonNegative(input.framesPerSecond ?? undefined),
        freezeCount: integer(input.freezeCount),
        videoFreezeMs: nonNegative(input.videoFreezeMs ?? undefined),
        audioFreezeMs: nonNegative(input.audioFreezeMs ?? undefined),
    };
}
export function emptyStats() {
    return toRtcStats();
}
/**
 * Classify connection quality on the Agora 1-6 scale.
 *
 * Thresholds are deliberately conservative and documented in README.md:
 *  - very bad: loss >= 20% or rtt >= 800ms or jitter >= 400ms
 *  - bad:      loss >= 10% or rtt >= 400ms or jitter >= 200ms
 *  - poor:     loss >=  5% or rtt >= 250ms or jitter >= 100ms
 *  - good:     loss >=  2% or rtt >= 150ms or jitter >=  50ms
 *  - excellent otherwise
 */
export function classifyNetworkQuality(input) {
    if (input.connected === false)
        return NetworkQuality.Disconnected;
    if (input.measured === false)
        return NetworkQuality.Unknown;
    const loss = Math.min(1, Math.max(0, input.packetLoss));
    const rtt = Math.max(0, input.rttMs);
    const jitter = Math.max(0, input.jitterMs);
    if (loss >= 0.2 || rtt >= 800 || jitter >= 400)
        return NetworkQuality.VeryBad;
    if (loss >= 0.1 || rtt >= 400 || jitter >= 200)
        return NetworkQuality.Bad;
    if (loss >= 0.05 || rtt >= 250 || jitter >= 100)
        return NetworkQuality.Poor;
    if (loss >= 0.02 || rtt >= 150 || jitter >= 50)
        return NetworkQuality.Good;
    return NetworkQuality.Excellent;
}
/** Convenience wrapper that classifies a mapped snapshot. */
export function classifyStats(stats, connected = true) {
    const measured = stats.rtt > 0 || stats.packetLoss > 0 || stats.jitter > 0 || stats.bitrate > 0;
    return classifyNetworkQuality({
        rttMs: stats.rtt,
        packetLoss: stats.packetLoss,
        jitterMs: stats.jitter,
        connected,
        measured,
    });
}
/** Merge a partial stats snapshot over a base snapshot (test/utility helper). */
export function mergeStats(base, patch) {
    return toRtcStats({
        rttMs: patch.rttMs ?? base.rtt,
        packetLoss: patch.packetLoss ?? base.packetLoss,
        jitterMs: patch.jitterMs ?? base.jitter,
        bitrateKbps: patch.bitrateKbps ?? base.bitrate,
        width: patch.width ?? base.resolution.width,
        height: patch.height ?? base.resolution.height,
        framesPerSecond: patch.framesPerSecond ?? base.framesPerSecond,
        freezeCount: patch.freezeCount ?? base.freezeCount,
        videoFreezeMs: patch.videoFreezeMs ?? 0,
        audioFreezeMs: patch.audioFreezeMs ?? 0,
    });
}
//# sourceMappingURL=stats.js.map